import React, { useState } from "react"
import { graphql, StaticQuery } from "gatsby"
import { createStyles, WithStyles, withStyles } from "@material-ui/styles"
import classNames from "classnames"
import BackgroundImage from "gatsby-background-image"
import { Transition } from "react-transition-group"

import EmailIcon from "../images/icons/email.svg"
import LinkedInIcon from "../images/icons/linkedIn.svg"
import FacebookIcon from "../images/icons/facebook.svg"
import BehanceIcon from "../images/icons/behance.svg"
import ArtStationIcon from "../images/icons/artStation.svg"
import ThunderIcon from "../images/icons/thunder.svg"

import backgroundGIF from "../images/team/background.gif"

const styles = createStyles({
  teamMember: {
    width: "100%",
    maxWidth: 308,
    "@media (max-width: 479.95px)": {
      maxWidth: 250,
    },
  },
  image: {
    position: "relative",
    display: "flex !important" as "flex",
    alignItems: "flex-end",
    overflow: "hidden",
    borderRadius: 25,
    marginBottom: 20,
    width: "100%",
    background: "linear-gradient(0deg, #FFFFFF -0.01%, #EBEBEB 100%)",
    height: 300,
    transitionDuration: "0.5s",
    msTransitionTimingFunction: "ease-in-out",
    transitionProperty: "opacity",
    "@media (max-width: 479.95px)": {
      height: 243,
    },
  },
  backgroundImage: {
    position: "absolute !important" as "absolute",
    top: 0,
    left: 0,
    transitionDuration: "0.5s",
    msTransitionTimingFunction: "ease-in-out",
    transitionProperty: "opacity, filter",
    width: "100% !important",
    height: "100% !important",
  },
  memberPhoto: {
    display: "flex !important" as "flex",
    justifyContent: "center",
    alignItems: "flex-end",
    filter: "grayscale(1)",
    opacity: 0.6,
    transitionDuration: "0.5s",
    msTransitionTimingFunction: "ease-in-out",
    transitionProperty: "opacity, filter",
    width: "100% !important",
    height: "100% !important",
  },
  teamMemberContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    fontSize: "1rem",
    lineHeight: "1.72rem",
  },
  socials: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 25,
    zIndex: 1,
    transition: "opacity 0.5s ease-in-out",
  },
  socialsIcon: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: 34,
    height: 34,
    borderRadius: "50%",
    margin: "0 7.5px",
    border: "none",
    backgroundColor: "#FFFFFF",
    fill: "#494949",
    cursor: "pointer",
    outline: "none",
    padding: 8,
    "&:hover": {
      backgroundColor: "#FF7900",
      fill: "#FFFFFF",
    },
  },
  nameContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  name: {
    fontWeight: 800,
    fontSize: "1.22rem",
    lineHeight: "2.11rem",
    color: "#606060",
  },
  thunderIcon: {
    margin: "0 10px",
  },
  title: {
    color: "#A8A8A8",
  },
  description: {
    fontSize: "0.78rem",
    lineHeight: "1.02rem",
    color: "#898989",
    textAlign: "center",
  },
})

export interface TeamMember {
  name: string
  title: string
  description: string
  imageName: string
  socials: Socials
}

export interface Socials {
  email?: string
  linkedIn?: string
  facebook?: string
  behance?: string
  artStation?: string
}

export interface Props {
  member: TeamMember
}

type PropsAndStyles = Props & WithStyles<typeof styles>

const TeamListItem = ({ classes, member }: PropsAndStyles) => {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <StaticQuery
      query={graphql`
        query {
          images: allFile(filter: { sourceInstanceName: { eq: "images" } }) {
            edges {
              node {
                relativePath
                childImageSharp {
                  fixed(width: 372, quality: 100) {
                    width
                    height
                    ...GatsbyImageSharpFixed
                  }
                }
              }
            }
          }
        }
      `}
      render={data => {
        const { name, title, description, imageName, socials } = member

        const memberPhoto = data.images.edges.find(
          image => image.node.relativePath === `team/members/${imageName}.png`
        )

        return (
          <div
            className={classes.teamMember}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <Transition in={isHovered}>
              {state => {
                const active = state !== "exited" && state !== "exiting"

                return (
                  <div
                    className={classes.image}
                    style={{
                      opacity: active ? 1 : 0.5,
                    }}
                  >
                    <img
                      className={classes.backgroundImage}
                      src={backgroundGIF}
                      style={{
                        opacity: active ? 1 : 0,
                        filter: `grayscale(${active ? 0 : 1})`,
                      }}
                    />
                    <BackgroundImage
                      className={classNames(
                        memberPhoto?.node.childImageSharp.fixed,
                        classes.memberPhoto
                      )}
                      style={{
                        opacity: active ? 1 : 0.6,
                        filter: `grayscale(${active ? 0 : 1})`,
                      }}
                      fixed={memberPhoto?.node.childImageSharp.fixed}
                    >
                      <div
                        className={classes.socials}
                        style={{
                          opacity: active ? 1 : 0,
                        }}
                      >
                        {socials.email && (
                          <a
                            className={classes.socialsIcon}
                            href={`mailto:${socials.email}`}
                          >
                            <EmailIcon />
                          </a>
                        )}
                        {socials.linkedIn && (
                          <a
                            className={classes.socialsIcon}
                            href={socials.linkedIn}
                            target="_blank"
                          >
                            <LinkedInIcon />
                          </a>
                        )}
                        {socials.facebook && (
                          <a
                            className={classes.socialsIcon}
                            href={socials.facebook}
                            target="_blank"
                          >
                            <FacebookIcon />
                          </a>
                        )}
                        {socials.behance && (
                          <a
                            className={classes.socialsIcon}
                            href={socials.behance}
                            target="_blank"
                          >
                            <BehanceIcon />
                          </a>
                        )}
                        {socials.artStation && (
                          <a
                            className={classes.socialsIcon}
                            href={socials.artStation}
                            target="_blank"
                          >
                            <ArtStationIcon />
                          </a>
                        )}
                      </div>
                    </BackgroundImage>
                  </div>
                )
              }}
            </Transition>

            <div className={classes.teamMemberContent}>
              <div className={classes.nameContainer}>
                <div className={classes.name}>{name}</div>
                {description && (
                  <>
                    <Transition in={isHovered}>
                      {state => {
                        const active = state !== "exited" && state !== "exiting"
                        return (
                          <ThunderIcon
                            className={classes.thunderIcon}
                            style={{
                              fill: active ? "#FF7900" : "#A8A8A8",
                              transition: "fill 0.5s ease-in-out",
                            }}
                          />
                        )
                      }}
                    </Transition>
                    <div className={classes.title}>{title}</div>
                  </>
                )}
              </div>

              {!description && <div className={classes.title}>{title}</div>}

              <div className={classes.description}>{description}</div>
            </div>
          </div>
        )
      }}
    />
  )
}

export default withStyles(styles)(TeamListItem)
